/**
 * GroupedTabNav — replaces the flat 49-tab DraggableTabBar in CRMPage.js
 * with a 7-category grouped nav.
 *
 * Each top-level group is either:
 *   • a standalone tab (no dropdown — single click activates it), OR
 *   • a category with a dropdown of sub-tabs
 *
 * Behavior
 *   • Click a group's button → toggles its dropdown
 *   • Click outside or press Escape → closes any open dropdown
 *   • Click a sub-tab → activates it AND closes the dropdown
 *   • Breadcrumb under the row shows "Group › Active Tab" when applicable
 *   • Active group + active sub-tab are visually highlighted
 *   • Tab IDs are stable — same as the legacy flat TABS array so deep links
 *     and saved state continue to work
 *
 * Props
 *   groups       — array of { id, label, icon, tabs?: [{ id, label, icon }] }
 *   activeTab    — current tab id (string)
 *   onTabChange  — (newTabId) => void
 *   neuShadow    — boxShadow value for active elements (passed in to match
 *                  the parent's neumorphic theme)
 *   neuShadowInset — boxShadow value for the bar container
 */
"use strict";

import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

function GroupedTabNav({
  groups,
  activeTab,
  onTabChange,
  neuShadow,
  neuShadowInset,
}) {
  const [openGroup, setOpenGroup] = useState(null);
  const containerRef = useRef(null);

  // ── Locate which group owns the active tab + label for breadcrumb ────
  let activeGroupId = null;
  let activeTabLabel = null;
  let activeIsStandalone = false;
  for (const g of groups) {
    if (g.id === activeTab) {
      activeGroupId = g.id;
      activeTabLabel = g.label;
      activeIsStandalone = true;
      break;
    }
    if (Array.isArray(g.tabs)) {
      const found = g.tabs.find((t) => t.id === activeTab);
      if (found) {
        activeGroupId = g.id;
        activeTabLabel = found.label;
        break;
      }
    }
  }
  const activeGroup = groups.find((g) => g.id === activeGroupId);

  // ── Close dropdown on outside-click or Escape ────────────────────────
  useEffect(() => {
    if (openGroup === null) return;
    function onDoc(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenGroup(null);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") setOpenGroup(null);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [openGroup]);

  // ── Shared style helpers ─────────────────────────────────────────────
  const groupBtnStyle = (isActive, isOpen) => ({
    padding: "8px 14px",
    border: "none",
    borderRadius: 10,
    background: isActive || isOpen ? "#F8F9FA" : "transparent",
    boxShadow: isActive ? neuShadow : "none",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: isActive ? 600 : 500,
    color: isActive ? "#2E8B98" : "#495057",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  });

  const subTabStyle = (isActive) => ({
    padding: "9px 14px",
    border: "none",
    borderRadius: 8,
    background: isActive ? "rgba(46, 139, 152, 0.10)" : "transparent",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: isActive ? 600 : 500,
    color: isActive ? "#2E8B98" : "#495057",
    display: "flex",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
    transition: "background 0.12s ease",
    textAlign: "left",
    width: "100%",
  });

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div ref={containerRef}>
      {/* Top-level group row */}
      <div
        role="tablist"
        aria-label="CRM sections"
        style={{
          display: "flex",
          gap: 4,
          flexWrap: "wrap",
          background: "#F8F9FA",
          borderRadius: 14,
          boxShadow: neuShadowInset,
          padding: "5px 7px",
        }}
      >
        {groups.map((group) => {
          const isActive = group.id === activeGroupId;
          const isOpen = group.id === openGroup;
          const Icon = group.icon;

          // Standalone group (no sub-tabs) — single click activates the tab
          if (!Array.isArray(group.tabs) || group.tabs.length === 0) {
            return (
              <button
                key={group.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => {
                  setOpenGroup(null);
                  onTabChange(group.id);
                }}
                style={groupBtnStyle(isActive, false)}
              >
                {Icon && <Icon size={14} />}
                <span>{group.label}</span>
              </button>
            );
          }

          // Category group with dropdown
          return (
            <div key={group.id} style={{ position: "relative" }}>
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => setOpenGroup(isOpen ? null : group.id)}
                style={groupBtnStyle(isActive, isOpen)}
              >
                {Icon && <Icon size={14} />}
                <span>{group.label}</span>
                <ChevronDown
                  size={12}
                  style={{
                    transition: "transform 0.15s ease",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </button>

              {isOpen && (
                <div
                  role="menu"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    minWidth: 230,
                    background: "#FFFFFF",
                    borderRadius: 12,
                    boxShadow:
                      "0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)",
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                    padding: 6,
                    zIndex: 50,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {group.tabs.map((t) => {
                    const TIcon = t.icon;
                    const isSubActive = t.id === activeTab;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setOpenGroup(null);
                          onTabChange(t.id);
                        }}
                        style={subTabStyle(isSubActive)}
                        onMouseEnter={(e) => {
                          if (!isSubActive) {
                            e.currentTarget.style.background = "rgba(46, 139, 152, 0.06)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSubActive) {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        {TIcon && <TIcon size={13} />}
                        <span>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Breadcrumb (only when active tab lives inside a category, and
          the sub-tab name actually adds information beyond the group). */}
      {activeGroup && !activeIsStandalone && activeTabLabel !== activeGroup.label && (
        <div
          style={{
            marginTop: 8,
            padding: "0 4px",
            fontSize: 11,
            color: "#6C757D",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span style={{ fontWeight: 500 }}>{activeGroup.label}</span>
          <ChevronRight size={11} />
          <span style={{ color: "#2E8B98", fontWeight: 600 }}>{activeTabLabel}</span>
        </div>
      )}
    </div>
  );
}

export default GroupedTabNav;
